/**
 * Stats Service
 * Queries real Firestore data to provide platform statistics
 */

import { db } from '@/lib/firebase';
import { catalogueDate, monthBuckets } from '@/lib/catalogue-analytics';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  getCountFromServer,
  startAfter,
} from 'firebase/firestore';

// ============ TYPES ============

export interface AppStats {
  users: number;
  artists: number;
  releases: number;
  works: number;
  events: number;
  news: number;
  feedPosts: number;
  syncBriefs: number;
  syncTracks: number;
  chatMessages: number;
  favorites: number;
  follows: number;
  marketplaceItems: number;
  lastUpdated: Date;
  unavailable?: string[];
}

export interface WorkHourEntry {
  id: string;
  userId: string;
  userName: string;
  date: string; // YYYY-MM-DD format
  hours: number;
  description: string;
  category: 'Development' | 'Design' | 'Content' | 'Marketing' | 'Admin' | 'Support';
  createdAt?: Timestamp;
}

export type CostCategory =
  | 'Hosting' | 'Database' | 'AI Tokens' | 'Domain' | 'CDN/Storage' | 'APIs'
  | 'Marketing' | 'Legal' | 'Transportation' | 'Representation'
  | 'Office/Equipment' | 'Insurance' | 'Accounting' | 'Software Licenses'
  | 'Contractor/Freelancer' | 'Development' | 'Other';

export interface PlatformCost {
  entryKind?: 'actual' | 'budget';
  paymentStatus?: 'paid' | 'unpaid';
  invoiceRef?: string;
  sourceFileName?: string;
  endDate?: string;
  id: string;
  name: string;
  category: CostCategory;
  amount: number;
  currency: string;
  frequency: 'monthly' | 'yearly' | 'one-time' | 'per-use';
  startDate: string; // YYYY-MM-DD format
  notes?: string;
  addedBy: string;
  addedByUserId?: string;
  taxDeductible?: boolean; // For Swedish tax deductions (avdrag)
  createdAt?: Timestamp;
}

export interface PlatformRevenue {
  id: string;
  source: string;
  amount: number; // gross / turnover, in major currency units
  currency: string;
  date: string; // YYYY-MM-DD format
  notes?: string;
  addedBy: string;
  addedByUserId?: string;
  createdAt?: Timestamp;
  /** BB's actual income on this entry — the π% cut on a creator sale. Manual
   *  entries have no cut (the whole amount is BB's), so this equals `amount`. */
  platformCut?: number;
  /** True when the entry is derived from a real purchase record rather than
   *  typed in by hand. Derived entries are read-only in the admin UI. */
  derived?: boolean;
}

// ============ MULTI-COUNTRY TAX BOOKKEEPING ============

export type TaxCountry = 'SE' | 'US' | 'GB' | 'DE' | 'FR' | 'JP' | 'KR' | 'BR' | 'NG' | 'CA' | 'AU' | 'ES' | 'IT' | 'NL' | 'JM' | 'CO' | 'MX' | 'PR';

export interface TaxCountryConfig {
  code: TaxCountry;
  name: string;
  currency: string;
  vatName: string; // "Moms", "VAT", "GST", "Sales Tax", etc.
  vatRates: number[]; // e.g. [25, 12, 6, 0] for Sweden
  fiscalYearFormat: 'calendar' | 'april-march' | 'july-june' | 'october-september' | 'custom';
  taxAuthority: string; // "Skatteverket", "IRS", "HMRC", etc.
  categories: { key: string; label: string; localLabel?: string }[];
}

export const TAX_COUNTRY_CONFIGS: Record<TaxCountry, TaxCountryConfig> = {
  SE: {
    code: 'SE', name: 'Sweden', currency: 'SEK', vatName: 'Moms', vatRates: [25, 12, 6, 0],
    fiscalYearFormat: 'calendar', taxAuthority: 'Skatteverket',
    categories: [
      { key: 'revenue', label: 'Revenue', localLabel: 'Intäkter' },
      { key: 'staff', label: 'Staff Costs', localLabel: 'Personalkostnader' },
      { key: 'premises', label: 'Premises', localLabel: 'Lokalkostnader' },
      { key: 'external', label: 'Other External Costs', localLabel: 'Övriga externa kostnader' },
      { key: 'representation', label: 'Representation', localLabel: 'Representation' },
      { key: 'travel', label: 'Travel & Transport', localLabel: 'Resor och transport' },
      { key: 'equipment', label: 'Equipment', localLabel: 'Förbrukningsinventarier' },
      { key: 'marketing', label: 'Marketing', localLabel: 'Reklam och marknadsföring' },
      { key: 'it', label: 'IT & Telecom', localLabel: 'IT och telekommunikation' },
      { key: 'insurance', label: 'Insurance', localLabel: 'Försäkringar' },
      { key: 'depreciation', label: 'Depreciation', localLabel: 'Avskrivningar' },
      { key: 'other', label: 'Other', localLabel: 'Övrigt' },
    ],
  },
  US: {
    code: 'US', name: 'United States', currency: 'USD', vatName: 'Sales Tax', vatRates: [0],
    fiscalYearFormat: 'calendar', taxAuthority: 'IRS',
    categories: [
      { key: 'revenue', label: 'Income / Revenue' },
      { key: 'cogs', label: 'Cost of Goods Sold' },
      { key: 'wages', label: 'Wages & Salaries' },
      { key: 'rent', label: 'Rent & Lease' },
      { key: 'utilities', label: 'Utilities' },
      { key: 'travel', label: 'Travel & Meals' },
      { key: 'equipment', label: 'Equipment & Supplies' },
      { key: 'marketing', label: 'Advertising & Marketing' },
      { key: 'professional', label: 'Professional Services' },
      { key: 'insurance', label: 'Insurance' },
      { key: 'depreciation', label: 'Depreciation & Amortization' },
      { key: 'other', label: 'Other Deductions' },
    ],
  },
  GB: {
    code: 'GB', name: 'United Kingdom', currency: 'GBP', vatName: 'VAT', vatRates: [20, 5, 0],
    fiscalYearFormat: 'april-march', taxAuthority: 'HMRC',
    categories: [
      { key: 'revenue', label: 'Turnover / Income' },
      { key: 'cogs', label: 'Cost of Sales' },
      { key: 'staff', label: 'Staff Costs' },
      { key: 'premises', label: 'Premises Costs' },
      { key: 'travel', label: 'Travel & Subsistence' },
      { key: 'entertainment', label: 'Entertainment' },
      { key: 'equipment', label: 'Equipment & IT' },
      { key: 'marketing', label: 'Advertising & Marketing' },
      { key: 'professional', label: 'Professional Fees' },
      { key: 'insurance', label: 'Insurance' },
      { key: 'depreciation', label: 'Depreciation' },
      { key: 'other', label: 'Other Expenses' },
    ],
  },
  DE: {
    code: 'DE', name: 'Germany', currency: 'EUR', vatName: 'MwSt/USt', vatRates: [19, 7, 0],
    fiscalYearFormat: 'calendar', taxAuthority: 'Finanzamt',
    categories: [
      { key: 'revenue', label: 'Revenue', localLabel: 'Umsatzerlöse' },
      { key: 'staff', label: 'Staff Costs', localLabel: 'Personalkosten' },
      { key: 'premises', label: 'Premises', localLabel: 'Raumkosten' },
      { key: 'travel', label: 'Travel', localLabel: 'Reisekosten' },
      { key: 'representation', label: 'Entertainment', localLabel: 'Bewirtungskosten' },
      { key: 'equipment', label: 'Equipment', localLabel: 'Betriebsbedarf' },
      { key: 'marketing', label: 'Marketing', localLabel: 'Werbung' },
      { key: 'insurance', label: 'Insurance', localLabel: 'Versicherungen' },
      { key: 'depreciation', label: 'Depreciation', localLabel: 'Abschreibungen' },
      { key: 'other', label: 'Other', localLabel: 'Sonstiges' },
    ],
  },
  FR: {
    code: 'FR', name: 'France', currency: 'EUR', vatName: 'TVA', vatRates: [20, 10, 5.5, 2.1, 0],
    fiscalYearFormat: 'calendar', taxAuthority: 'Direction Générale des Finances Publiques',
    categories: [
      { key: 'revenue', label: 'Revenue', localLabel: 'Chiffre d\'affaires' },
      { key: 'staff', label: 'Staff', localLabel: 'Charges de personnel' },
      { key: 'premises', label: 'Premises', localLabel: 'Charges locatives' },
      { key: 'travel', label: 'Travel', localLabel: 'Frais de déplacement' },
      { key: 'equipment', label: 'Equipment', localLabel: 'Fournitures' },
      { key: 'marketing', label: 'Marketing', localLabel: 'Publicité' },
      { key: 'insurance', label: 'Insurance', localLabel: 'Assurances' },
      { key: 'depreciation', label: 'Depreciation', localLabel: 'Amortissements' },
      { key: 'other', label: 'Other', localLabel: 'Autres charges' },
    ],
  },
  JP: {
    code: 'JP', name: 'Japan', currency: 'JPY', vatName: 'Consumption Tax', vatRates: [10, 8, 0],
    fiscalYearFormat: 'calendar', taxAuthority: 'National Tax Agency (国税庁)',
    categories: [
      { key: 'revenue', label: 'Revenue', localLabel: '売上高' },
      { key: 'cogs', label: 'Cost of Sales', localLabel: '売上原価' },
      { key: 'staff', label: 'Personnel', localLabel: '人件費' },
      { key: 'premises', label: 'Rent', localLabel: '地代家賃' },
      { key: 'travel', label: 'Travel', localLabel: '旅費交通費' },
      { key: 'entertainment', label: 'Entertainment', localLabel: '交際費' },
      { key: 'equipment', label: 'Supplies', localLabel: '消耗品費' },
      { key: 'marketing', label: 'Advertising', localLabel: '広告宣伝費' },
      { key: 'depreciation', label: 'Depreciation', localLabel: '減価償却費' },
      { key: 'other', label: 'Other', localLabel: 'その他' },
    ],
  },
  KR: {
    code: 'KR', name: 'South Korea', currency: 'KRW', vatName: 'VAT (부가세)', vatRates: [10, 0],
    fiscalYearFormat: 'calendar', taxAuthority: 'National Tax Service (국세청)',
    categories: [
      { key: 'revenue', label: 'Revenue', localLabel: '매출' },
      { key: 'cogs', label: 'Cost of Sales', localLabel: '매출원가' },
      { key: 'staff', label: 'Personnel', localLabel: '인건비' },
      { key: 'premises', label: 'Rent', localLabel: '임차료' },
      { key: 'travel', label: 'Travel', localLabel: '여비교통비' },
      { key: 'entertainment', label: 'Entertainment', localLabel: '접대비' },
      { key: 'equipment', label: 'Supplies', localLabel: '소모품비' },
      { key: 'marketing', label: 'Advertising', localLabel: '광고선전비' },
      { key: 'depreciation', label: 'Depreciation', localLabel: '감가상각비' },
      { key: 'other', label: 'Other', localLabel: '기타' },
    ],
  },
  BR: {
    code: 'BR', name: 'Brazil', currency: 'BRL', vatName: 'ICMS/ISS', vatRates: [18, 12, 7, 5, 0],
    fiscalYearFormat: 'calendar', taxAuthority: 'Receita Federal',
    categories: [
      { key: 'revenue', label: 'Revenue', localLabel: 'Receita' },
      { key: 'staff', label: 'Staff', localLabel: 'Despesas com pessoal' },
      { key: 'premises', label: 'Rent', localLabel: 'Aluguel' },
      { key: 'travel', label: 'Travel', localLabel: 'Viagens' },
      { key: 'equipment', label: 'Equipment', localLabel: 'Equipamentos' },
      { key: 'marketing', label: 'Marketing', localLabel: 'Marketing' },
      { key: 'insurance', label: 'Insurance', localLabel: 'Seguros' },
      { key: 'other', label: 'Other', localLabel: 'Outros' },
    ],
  },
  NG: {
    code: 'NG', name: 'Nigeria', currency: 'NGN', vatName: 'VAT', vatRates: [7.5, 0],
    fiscalYearFormat: 'calendar', taxAuthority: 'FIRS',
    categories: [
      { key: 'revenue', label: 'Revenue' },
      { key: 'cogs', label: 'Cost of Sales' },
      { key: 'staff', label: 'Staff Costs' },
      { key: 'premises', label: 'Rent & Utilities' },
      { key: 'travel', label: 'Travel & Transport' },
      { key: 'equipment', label: 'Equipment & Supplies' },
      { key: 'marketing', label: 'Marketing' },
      { key: 'professional', label: 'Professional Fees' },
      { key: 'other', label: 'Other Expenses' },
    ],
  },
  CA: {
    code: 'CA', name: 'Canada', currency: 'CAD', vatName: 'GST/HST', vatRates: [15, 13, 12, 5, 0],
    fiscalYearFormat: 'calendar', taxAuthority: 'CRA',
    categories: [
      { key: 'revenue', label: 'Revenue' },
      { key: 'cogs', label: 'Cost of Goods Sold' },
      { key: 'staff', label: 'Salaries & Wages' },
      { key: 'premises', label: 'Office & Rent' },
      { key: 'travel', label: 'Travel & Vehicle' },
      { key: 'meals', label: 'Meals & Entertainment' },
      { key: 'equipment', label: 'Equipment & Supplies' },
      { key: 'marketing', label: 'Advertising' },
      { key: 'professional', label: 'Professional Fees' },
      { key: 'insurance', label: 'Insurance' },
      { key: 'depreciation', label: 'CCA (Depreciation)' },
      { key: 'other', label: 'Other Expenses' },
    ],
  },
  AU: {
    code: 'AU', name: 'Australia', currency: 'AUD', vatName: 'GST', vatRates: [10, 0],
    fiscalYearFormat: 'july-june', taxAuthority: 'ATO',
    categories: [
      { key: 'revenue', label: 'Income' },
      { key: 'cogs', label: 'Cost of Sales' },
      { key: 'staff', label: 'Employee Expenses' },
      { key: 'premises', label: 'Rent & Occupancy' },
      { key: 'travel', label: 'Travel' },
      { key: 'equipment', label: 'Equipment & Supplies' },
      { key: 'marketing', label: 'Advertising & Promotion' },
      { key: 'professional', label: 'Professional Services' },
      { key: 'insurance', label: 'Insurance' },
      { key: 'depreciation', label: 'Depreciation' },
      { key: 'other', label: 'Other Deductions' },
    ],
  },
  ES: {
    code: 'ES', name: 'Spain', currency: 'EUR', vatName: 'IVA', vatRates: [21, 10, 4, 0],
    fiscalYearFormat: 'calendar', taxAuthority: 'Agencia Tributaria',
    categories: [
      { key: 'revenue', label: 'Revenue', localLabel: 'Ingresos' },
      { key: 'staff', label: 'Staff', localLabel: 'Gastos de personal' },
      { key: 'premises', label: 'Premises', localLabel: 'Alquiler' },
      { key: 'travel', label: 'Travel', localLabel: 'Gastos de viaje' },
      { key: 'equipment', label: 'Equipment', localLabel: 'Suministros' },
      { key: 'marketing', label: 'Marketing', localLabel: 'Publicidad' },
      { key: 'insurance', label: 'Insurance', localLabel: 'Seguros' },
      { key: 'depreciation', label: 'Depreciation', localLabel: 'Amortización' },
      { key: 'other', label: 'Other', localLabel: 'Otros gastos' },
    ],
  },
  IT: {
    code: 'IT', name: 'Italy', currency: 'EUR', vatName: 'IVA', vatRates: [22, 10, 5, 4, 0],
    fiscalYearFormat: 'calendar', taxAuthority: 'Agenzia delle Entrate',
    categories: [
      { key: 'revenue', label: 'Revenue', localLabel: 'Ricavi' },
      { key: 'staff', label: 'Staff', localLabel: 'Costo del personale' },
      { key: 'premises', label: 'Premises', localLabel: 'Affitto' },
      { key: 'travel', label: 'Travel', localLabel: 'Spese di viaggio' },
      { key: 'equipment', label: 'Equipment', localLabel: 'Attrezzature' },
      { key: 'marketing', label: 'Marketing', localLabel: 'Pubblicità' },
      { key: 'insurance', label: 'Insurance', localLabel: 'Assicurazioni' },
      { key: 'depreciation', label: 'Depreciation', localLabel: 'Ammortamenti' },
      { key: 'other', label: 'Other', localLabel: 'Altre spese' },
    ],
  },
  NL: {
    code: 'NL', name: 'Netherlands', currency: 'EUR', vatName: 'BTW', vatRates: [21, 9, 0],
    fiscalYearFormat: 'calendar', taxAuthority: 'Belastingdienst',
    categories: [
      { key: 'revenue', label: 'Revenue', localLabel: 'Omzet' },
      { key: 'staff', label: 'Staff', localLabel: 'Personeelskosten' },
      { key: 'premises', label: 'Premises', localLabel: 'Huisvestingskosten' },
      { key: 'travel', label: 'Travel', localLabel: 'Reiskosten' },
      { key: 'equipment', label: 'Equipment', localLabel: 'Kantoorbenodigdheden' },
      { key: 'marketing', label: 'Marketing', localLabel: 'Marketing' },
      { key: 'insurance', label: 'Insurance', localLabel: 'Verzekeringen' },
      { key: 'depreciation', label: 'Depreciation', localLabel: 'Afschrijvingen' },
      { key: 'other', label: 'Other', localLabel: 'Overige kosten' },
    ],
  },
  JM: {
    code: 'JM', name: 'Jamaica', currency: 'JMD', vatName: 'GCT', vatRates: [15, 0],
    fiscalYearFormat: 'april-march', taxAuthority: 'TAJ',
    categories: [
      { key: 'revenue', label: 'Revenue' },
      { key: 'cogs', label: 'Cost of Sales' },
      { key: 'staff', label: 'Employee Costs' },
      { key: 'premises', label: 'Rent & Utilities' },
      { key: 'travel', label: 'Travel' },
      { key: 'equipment', label: 'Equipment' },
      { key: 'marketing', label: 'Marketing' },
      { key: 'other', label: 'Other' },
    ],
  },
  CO: {
    code: 'CO', name: 'Colombia', currency: 'COP', vatName: 'IVA', vatRates: [19, 5, 0],
    fiscalYearFormat: 'calendar', taxAuthority: 'DIAN',
    categories: [
      { key: 'revenue', label: 'Revenue', localLabel: 'Ingresos' },
      { key: 'staff', label: 'Staff', localLabel: 'Gastos de personal' },
      { key: 'premises', label: 'Premises', localLabel: 'Arrendamiento' },
      { key: 'travel', label: 'Travel', localLabel: 'Gastos de viaje' },
      { key: 'equipment', label: 'Equipment', localLabel: 'Equipos' },
      { key: 'marketing', label: 'Marketing', localLabel: 'Publicidad' },
      { key: 'other', label: 'Other', localLabel: 'Otros gastos' },
    ],
  },
  MX: {
    code: 'MX', name: 'Mexico', currency: 'MXN', vatName: 'IVA', vatRates: [16, 0],
    fiscalYearFormat: 'calendar', taxAuthority: 'SAT',
    categories: [
      { key: 'revenue', label: 'Revenue', localLabel: 'Ingresos' },
      { key: 'cogs', label: 'Cost of Sales', localLabel: 'Costo de ventas' },
      { key: 'staff', label: 'Staff', localLabel: 'Gastos de personal' },
      { key: 'premises', label: 'Premises', localLabel: 'Arrendamiento' },
      { key: 'travel', label: 'Travel', localLabel: 'Gastos de viaje' },
      { key: 'equipment', label: 'Equipment', localLabel: 'Equipo' },
      { key: 'marketing', label: 'Marketing', localLabel: 'Publicidad' },
      { key: 'other', label: 'Other', localLabel: 'Otros gastos' },
    ],
  },
  PR: {
    code: 'PR', name: 'Puerto Rico', currency: 'USD', vatName: 'IVU/SUT', vatRates: [11.5, 1, 0],
    fiscalYearFormat: 'calendar', taxAuthority: 'Hacienda',
    categories: [
      { key: 'revenue', label: 'Revenue / Ingresos' },
      { key: 'cogs', label: 'Cost of Goods Sold' },
      { key: 'staff', label: 'Salaries & Wages' },
      { key: 'premises', label: 'Rent & Utilities' },
      { key: 'travel', label: 'Travel & Transport' },
      { key: 'equipment', label: 'Equipment & Supplies' },
      { key: 'marketing', label: 'Marketing' },
      { key: 'professional', label: 'Professional Services' },
      { key: 'other', label: 'Other Expenses' },
    ],
  },
};

export interface BookkeepingEntry {
  id: string;
  date: string;
  description: string;
  category: string; // category key from TaxCountryConfig.categories
  amount: number;
  currency: string;
  country: TaxCountry;
  vatRate?: number;
  vatAmount?: number;
  isDeductible: boolean;
  receipt?: string; // URL to receipt image
  fiscalYear: string; // e.g. "2025" or "2025-2026"
  addedBy: string;
  addedByUserId?: string;
  createdAt?: Timestamp;
}

// ============ APP STATS ============

/**
 * Get counts of documents in all collections
 */
export async function getAppStats(): Promise<AppStats> {
  try {
    const collectionNames = [
      'users', 'artists', 'releases', 'works', 'events', 'news',
      'feedPosts', 'syncBriefs', 'syncTracks', 'chatMessages',
      'favorites', 'follows', 'item',
    ] as const;

    // Independent counts; unavailable collections are reported separately from zero.
    const [snapshots, users, profiles] = await Promise.all([
      Promise.all(collectionNames.map(name => getCountFromServer(collection(db, name)).catch(() => null))),
      getDocs(collection(db, 'users')).catch(() => null),
      getDocs(collection(db, 'userProfiles')).catch(() => null),
    ]);
    const counts: Record<string, number> = {};
    const unavailable: string[] = [];
    collectionNames.forEach((name, i) => {
      counts[name] = snapshots[i]?.data().count ?? 0;
      if (!snapshots[i]) unavailable.push(name);
    });
    // Both legacy and current account stores use UID document ids. Count their
    // union — from whichever loaded; when neither may be read here (visitors),
    // the server counts them (/api/stats/accounts, total only).
    // The server's count (Firebase Auth accounts) is the truth for everyone —
    // a visitor's browser can read only part of the profile collections
    // (Gordon saw "4"). The local union is only the fallback.
    let accountCount = 0;
    const fromServer = typeof window !== 'undefined'
      ? await fetch('/api/stats/accounts').then(r => (r.ok ? r.json() : null)).catch(() => null)
      : null;
    if (typeof fromServer?.accounts === 'number') {
      accountCount = fromServer.accounts;
    } else if (users || profiles) {
      accountCount = new Set([...(users?.docs ?? []), ...(profiles?.docs ?? [])].map(d => d.id)).size;
    }
    if (typeof fromServer?.accounts === 'number' || users || profiles) {
      const i = unavailable.indexOf('users');
      if (i >= 0) unavailable.splice(i, 1);
    } else if (!unavailable.includes('users')) {
      unavailable.push('users');
    }

    // Marketplace = everything listed there (products, people to book, venues,
    // magazines), counted on the server; the local product count is the fallback.
    const market = typeof window !== 'undefined'
      ? await fetch('/api/stats/marketplace').then(r => (r.ok ? r.json() : null)).catch(() => null)
      : null;
    const marketplaceTotal = typeof market?.total === 'number' ? market.total : counts['item'];
    if (typeof market?.total === 'number') {
      const i = unavailable.indexOf('item');
      if (i >= 0) unavailable.splice(i, 1);
    }

    return {
      users: accountCount,
      unavailable,
      artists: counts['artists'],
      releases: counts['releases'],
      works: counts['works'],
      events: counts['events'],
      news: counts['news'],
      feedPosts: counts['feedPosts'],
      syncBriefs: counts['syncBriefs'],
      syncTracks: counts['syncTracks'],
      chatMessages: counts['chatMessages'],
      favorites: counts['favorites'],
      follows: counts['follows'],
      marketplaceItems: marketplaceTotal,
      lastUpdated: new Date(),
    };
  } catch (error) {
    console.error('Error fetching app stats:', error);
    return {
      unavailable: ['users', 'artists', 'releases', 'works', 'events', 'news', 'feedPosts', 'syncBriefs', 'syncTracks', 'chatMessages', 'favorites', 'follows', 'item'],
      users: 0,
      artists: 0,
      releases: 0,
      works: 0,
      events: 0,
      news: 0,
      feedPosts: 0,
      syncBriefs: 0,
      syncTracks: 0,
      chatMessages: 0,
      favorites: 0,
      follows: 0,
      marketplaceItems: 0,
      lastUpdated: new Date(),
    };
  }
}

// ============ WORK HOURS ============

/**
 * Fetch work hour entries, optionally filtered by date range
 */
export async function getWorkHours(startDate?: Date, endDate?: Date): Promise<WorkHourEntry[]> {
  try {
    let q = query(
      collection(db, 'workHours'),
      orderBy('date', 'desc')
    );

    let snapshot = await getDocs(q);
    let entries = snapshot.docs.map(d => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt,
    } as WorkHourEntry));

    // Client-side filtering if date range provided
    if (startDate || endDate) {
      entries = entries.filter(entry => {
        const entryDate = new Date(entry.date);
        if (startDate && entryDate < startDate) return false;
        if (endDate && entryDate > endDate) return false;
        return true;
      });
    }

    return entries;
  } catch (error) {
    console.error('Error fetching work hours:', error);
    return [];
  }
}

/**
 * Add a work hour entry
 */
export async function addWorkHourEntry(entry: {
  userId: string;
  userName: string;
  date: string;
  hours: number;
  description: string;
  category: 'Development' | 'Design' | 'Content' | 'Marketing' | 'Admin' | 'Support';
}): Promise<string> {
  try {
    const docRef = await addDoc(collection(db, 'workHours'), {
      ...entry,
      createdAt: Timestamp.now(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding work hour entry:', error);
    throw error;
  }
}

/**
 * Delete a work hour entry
 */
export async function deleteWorkHourEntry(id: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'workHours', id));
  } catch (error) {
    console.error('Error deleting work hour entry:', error);
    throw error;
  }
}

// ============ PLATFORM COSTS ============

/**
 * Fetch platform cost entries, optionally filtered by date range
 */
export async function getPlatformCosts(startDate?: Date, endDate?: Date): Promise<PlatformCost[]> {
  try {
    let q = query(
      collection(db, 'platformCosts'),
      orderBy('startDate', 'desc')
    );

    let snapshot = await getDocs(q);
    let entries = snapshot.docs.map(d => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt,
    } as PlatformCost));

    // Client-side filtering if date range provided
    if (startDate || endDate) {
      entries = entries.filter(entry => {
        const entryDate = new Date(entry.startDate);
        if (startDate && entryDate < startDate) return false;
        if (endDate && entryDate > endDate) return false;
        return true;
      });
    }

    return entries;
  } catch (error) {
    console.error('Error fetching platform costs:', error);
    throw error;
  }
}

/**
 * Add a platform cost entry
 */
export async function addPlatformCost(entry: {
  name: string;
  category: CostCategory;
  amount: number;
  currency: string;
  frequency: 'monthly' | 'yearly' | 'one-time' | 'per-use';
  startDate: string;
  notes?: string;
  addedBy: string;
  addedByUserId?: string;
  taxDeductible?: boolean;
}): Promise<string> {
  try {
    const docRef = await addDoc(collection(db, 'platformCosts'), {
      ...entry,
      createdAt: Timestamp.now(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding platform cost:', error);
    throw error;
  }
}

/**
 * Update a platform cost entry
 */
export async function updatePlatformCost(id: string, data: Partial<any>): Promise<void> {
  try {
    await updateDoc(doc(db, 'platformCosts', id), data);
  } catch (error) {
    console.error('Error updating platform cost:', error);
    throw error;
  }
}

/**
 * Delete a platform cost entry
 */
export async function deletePlatformCost(id: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'platformCosts', id));
  } catch (error) {
    console.error('Error deleting platform cost:', error);
    throw error;
  }
}

// ============ PLATFORM REVENUE ============

/**
 * Fetch platform revenue entries, optionally filtered by date range
 */
export async function getPlatformRevenue(startDate?: Date, endDate?: Date): Promise<PlatformRevenue[]> {
  try {
    let q = query(
      collection(db, 'platformRevenue'),
      orderBy('date', 'desc')
    );

    // Manual entries typed into the admin panel, plus revenue DERIVED from
    // real purchases. Ordinary sales were never mirrored into platformRevenue
    // (only the resale flow wrote to it), so without the derived half every
    // track download and marketplace order was missing from the charts.
    const [snapshot, derived] = await Promise.all([
      getDocs(q),
      // Relative URL — client-side only. On the server this resolves to null
      // and the caller just gets the manual entries.
      typeof window === 'undefined'
        ? Promise.resolve(null)
        : fetch('/api/platform/revenue')
            .then(res => (res.ok ? res.json() : null))
            .catch(() => null) as Promise<{ ok: boolean; entries: { id: string; source: string; gross: number; platformCut: number; currency: string; date: string }[] } | null>,
    ]);

    let entries = snapshot.docs.map(d => ({
      id: d.id,
      ...d.data(),
      // Hand-entered revenue is BB's own income in full — there is no
      // creator payout to net off.
      platformCut: d.data().platformCut ?? d.data().amount,
      createdAt: d.data().createdAt,
    } as PlatformRevenue));

    if (derived?.ok && Array.isArray(derived.entries)) {
      entries = entries.concat(derived.entries.map(e => ({
        id: `derived:${e.id}`,
        source: e.source,
        amount: e.gross / 100,          // route returns integer cents
        platformCut: e.platformCut / 100,
        currency: e.currency,
        date: e.date,
        addedBy: 'auto (purchase record)',
        derived: true,
      } as PlatformRevenue)));
      entries.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    }

    // Client-side filtering if date range provided
    if (startDate || endDate) {
      entries = entries.filter(entry => {
        const entryDate = new Date(entry.date);
        if (startDate && entryDate < startDate) return false;
        if (endDate && entryDate > endDate) return false;
        return true;
      });
    }

    return entries;
  } catch (error) {
    console.error('Error fetching platform revenue:', error);
    return [];
  }
}

/**
 * Add a platform revenue entry
 */
export async function addPlatformRevenue(entry: {
  source: string;
  amount: number;
  currency: string;
  date: string;
  notes?: string;
  addedBy: string;
  addedByUserId?: string;
}): Promise<string> {
  try {
    const docRef = await addDoc(collection(db, 'platformRevenue'), {
      ...entry,
      createdAt: Timestamp.now(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding platform revenue:', error);
    throw error;
  }
}

/**
 * Delete a platform revenue entry
 */
export async function deletePlatformRevenue(id: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'platformRevenue', id));
  } catch (error) {
    console.error('Error deleting platform revenue:', error);
    throw error;
  }
}

// ============ BOOKKEEPING (Skatteverket / Deklaration) ============

/**
 * Fetch bookkeeping entries, optionally filtered by fiscal year
 */
export async function getBookkeepingEntries(fiscalYear?: string): Promise<BookkeepingEntry[]> {
  try {
    let q;
    if (fiscalYear) {
      q = query(
        collection(db, 'bookkeeping'),
        where('fiscalYear', '==', fiscalYear),
        orderBy('date', 'desc')
      );
    } else {
      q = query(collection(db, 'bookkeeping'), orderBy('date', 'desc'));
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as BookkeepingEntry));
  } catch (error) {
    // Fallback without orderBy (index may not exist)
    try {
      const snapshot = await getDocs(collection(db, 'bookkeeping'));
      let entries = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as BookkeepingEntry));
      if (fiscalYear) entries = entries.filter(e => e.fiscalYear === fiscalYear);
      return entries.sort((a, b) => b.date.localeCompare(a.date));
    } catch {
      return [];
    }
  }
}

/**
 * Add a bookkeeping entry
 */
export async function addBookkeepingEntry(entry: Omit<BookkeepingEntry, 'id' | 'createdAt'>): Promise<string> {
  try {
    const docRef = await addDoc(collection(db, 'bookkeeping'), {
      ...entry,
      createdAt: Timestamp.now(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding bookkeeping entry:', error);
    throw error;
  }
}

/**
 * Delete a bookkeeping entry
 */
export async function deleteBookkeepingEntry(id: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'bookkeeping', id));
  } catch (error) {
    console.error('Error deleting bookkeeping entry:', error);
    throw error;
  }
}

/**
 * Get fiscal year summary for tax declaration (multi-country)
 */
export async function getFiscalYearSummary(fiscalYear: string, country?: TaxCountry) {
  const entries = await getBookkeepingEntries(fiscalYear);
  const filtered = country ? entries.filter(e => e.country === country) : entries;

  const totalRevenue = filtered
    .filter(e => e.category === 'revenue')
    .reduce((sum, e) => sum + e.amount, 0);

  const totalExpenses = filtered
    .filter(e => e.category !== 'revenue')
    .reduce((sum, e) => sum + e.amount, 0);

  const totalVat = filtered.reduce((sum, e) => sum + (e.vatAmount || 0), 0);

  const deductibleExpenses = filtered
    .filter(e => e.isDeductible && e.category !== 'revenue')
    .reduce((sum, e) => sum + e.amount, 0);

  const byCategory: Record<string, number> = {};
  filtered.forEach(e => {
    byCategory[e.category] = (byCategory[e.category] || 0) + e.amount;
  });

  return {
    fiscalYear,
    country: country || 'SE',
    totalRevenue,
    totalExpenses,
    netResult: totalRevenue - totalExpenses,
    totalVat,
    deductibleExpenses,
    byCategory,
    entryCount: filtered.length,
  };
}

// ============ CREATOR BOOKKEEPING ============

export interface CreatorExpense {
  id: string;
  userId: string;
  date: string;
  description: string;
  category: 'Album Production' | 'Song Production' | 'Video Production' | 'App Development' |
    'Marketing' | 'Equipment' | 'Studio Rental' | 'Transportation' | 'Representation' |
    'Distribution' | 'Legal/Admin' | 'Other';
  amount: number;
  currency: string;
  projectId?: string; // link to album, song, video, or app
  projectType?: 'album' | 'song' | 'video' | 'app' | 'general';
  projectName?: string;
  receipt?: string;
  taxDeductible: boolean;
  createdAt?: Timestamp;
}

export interface CreatorRevenue {
  id: string;
  userId: string;
  date: string;
  source: string; // 'Streaming', 'Sync License', 'Marketplace Sale', 'Live Performance', etc.
  amount: number;
  currency: string;
  projectId?: string;
  projectType?: 'album' | 'song' | 'video' | 'app' | 'general';
  projectName?: string;
  notes?: string;
  createdAt?: Timestamp;
}

/**
 * Get creator expenses
 */
export async function getCreatorExpenses(userId: string): Promise<CreatorExpense[]> {
  try {
    const q = query(
      collection(db, 'creatorExpenses'),
      where('userId', '==', userId),
      orderBy('date', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as CreatorExpense));
  } catch {
    try {
      const q = query(collection(db, 'creatorExpenses'), where('userId', '==', userId));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as CreatorExpense))
        .sort((a, b) => b.date.localeCompare(a.date));
    } catch (error) { throw error; }
  }
}

/**
 * Add creator expense
 */
export async function addCreatorExpense(entry: Omit<CreatorExpense, 'id' | 'createdAt'>): Promise<string> {
  const docRef = await addDoc(collection(db, 'creatorExpenses'), {
    ...entry,
    createdAt: Timestamp.now(),
  });
  return docRef.id;
}

/**
 * Delete creator expense
 */
export async function deleteCreatorExpense(id: string): Promise<void> {
  await deleteDoc(doc(db, 'creatorExpenses', id));
}

/**
 * Get creator revenue entries
 */
export async function getCreatorRevenue(userId: string): Promise<CreatorRevenue[]> {
  try {
    const q = query(
      collection(db, 'creatorRevenue'),
      where('userId', '==', userId),
      orderBy('date', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as CreatorRevenue));
  } catch {
    try {
      const q = query(collection(db, 'creatorRevenue'), where('userId', '==', userId));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as CreatorRevenue))
        .sort((a, b) => b.date.localeCompare(a.date));
    } catch (error) { throw error; }
  }
}

/**
 * Add creator revenue
 */
export async function addCreatorRevenue(entry: Omit<CreatorRevenue, 'id' | 'createdAt'>): Promise<string> {
  const docRef = await addDoc(collection(db, 'creatorRevenue'), {
    ...entry,
    createdAt: Timestamp.now(),
  });
  return docRef.id;
}

/**
 * Delete creator revenue
 */
export async function deleteCreatorRevenue(id: string): Promise<void> {
  await deleteDoc(doc(db, 'creatorRevenue', id));
}

/**
 * Get creator financial summary
 */
export async function getCreatorFinancialSummary(userId: string) {
  const [expenses, revenue] = await Promise.all([
    getCreatorExpenses(userId),
    getCreatorRevenue(userId),
  ]);

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
  const totalRevenue = revenue.reduce((sum, r) => sum + r.amount, 0);

  const expensesByCategory: Record<string, number> = {};
  expenses.forEach(e => {
    expensesByCategory[e.category] = (expensesByCategory[e.category] || 0) + e.amount;
  });

  const expensesByProject: Record<string, number> = {};
  expenses.forEach(e => {
    const key = e.projectName || 'General';
    expensesByProject[key] = (expensesByProject[key] || 0) + e.amount;
  });

  const revenueBySource: Record<string, number> = {};
  revenue.forEach(r => {
    revenueBySource[r.source] = (revenueBySource[r.source] || 0) + r.amount;
  });

  return {
    totalExpenses,
    totalRevenue,
    netResult: totalRevenue - totalExpenses,
    expensesByCategory,
    expensesByProject,
    revenueBySource,
    expenseCount: expenses.length,
    revenueCount: revenue.length,
  };
}

// ============ MEDIA EVENT TRACKING ============

export type MediaEventType =
  | 'play'        // track started playing (fires once per play session)
  | 'complete'    // track played to end
  | 'click'       // any card/item was clicked/navigated to
  | 'view'        // page / detail view opened
  | 'search'      // search was performed
  | 'download'    // download triggered
  | 'share'       // share / copy-link triggered
  | 'favorite';   // hearted / unfavorited

export interface MediaEvent {
  id: string;
  type: MediaEventType;
  // What was acted on
  entityType: 'track' | 'release' | 'artist' | 'video' | 'news' | 'event' | 'playlist' | 'marketplace' | 'radio' | 'user' | 'other';
  entityId: string;
  entityTitle?: string;
  entityArtist?: string;
  entityGenre?: string;
  // Who did it
  userId?: string;
  // When
  timestamp: Timestamp;
  // Extra context
  section?: string;   // e.g. "new-releases", "featured-artists"
  durationMs?: number; // for plays: how long they actually listened
}

const EVENTS_COL = 'mediaEvents';

/**
 * Record a media event. Fire-and-forget — never throws so it never
 * blocks the user action that triggered it.
 */
export async function trackMediaEvent(
  event: Omit<MediaEvent, 'id' | 'timestamp'>
): Promise<void> {
  try {
    await addDoc(collection(db, EVENTS_COL), {
      ...event,
      timestamp: Timestamp.now(),
    });
  } catch {
    // Silently swallow — analytics must never break the main experience
  }
}

export interface MediaEventSummary {
  totalPlays: number;
  totalViews: number;
  totalClicks: number;
  totalDownloads: number;
  totalShares: number;
  totalFavorites: number;
  topTracks: Array<{ entityId: string; entityTitle: string; entityArtist: string; plays: number; }>;
  topArtists: Array<{ entityId: string; entityTitle: string; plays: number; }>;
  /** Demo/beat tracks + artists, kept separate from released music. */
  topDemos: Array<{ entityId: string; entityTitle: string; entityArtist: string; plays: number; }>;
  topDemoArtists: Array<{ entityId: string; entityTitle: string; plays: number; }>;
  topGenres: Array<{ genre: string; plays: number; }>;
  recentEvents: MediaEvent[];
  playsLast30Days: number;
  playsLast7Days: number;
  /** Monthly plays + downloads for the last 6 months — pre-computed to avoid a second round trip. */
  playsOverTime: Array<{ month: string; plays: number; downloads: number; }>;
}

/**
 * Aggregate media event stats for the admin dashboard.
 * Returns counts grouped by type and top-N entities.
 */
export async function getMediaEventStats(limitDays = 90, throwOnError = false): Promise<MediaEventSummary> {
  try {
    const now = Date.now();
    const since = new Date(now - limitDays * 24 * 60 * 60 * 1000);
    const months = monthBuckets(new Date(now));
    const trendStart = new Date(new Date(now).getFullYear(), new Date(now).getMonth() - 5, 1);
    const fetchStart = new Date(Math.min(since.getTime(), trendStart.getTime()));
    const constraints = [where('timestamp', '>=', Timestamp.fromDate(fetchStart)), orderBy('timestamp', 'desc')];
    // Fetch all pages; a busy catalogue must not silently lose events after 5,000.
    const allEvents: MediaEvent[] = [];
    let cursor: import('firebase/firestore').QueryDocumentSnapshot | undefined;
    while (true) {
      const snap = await getDocs(query(collection(db, EVENTS_COL), ...constraints, ...(cursor ? [startAfter(cursor)] : []), limit(1000)));
      allEvents.push(...snap.docs.map(d => ({ id: d.id, ...d.data() } as MediaEvent)));
      if (snap.size < 1000) break;
      cursor = snap.docs[snap.docs.length - 1];
    }
    const validEvents = allEvents.filter(e => {
      const date = catalogueDate(e.timestamp)?.getTime();
      return date !== undefined && date <= now;
    });
    const events = validEvents.filter(e => (catalogueDate(e.timestamp)?.getTime() ?? 0) >= since.getTime());

    const ms7  = 7  * 24 * 60 * 60 * 1000;
    const ms30 = 30 * 24 * 60 * 60 * 1000;

    // Counts
    const plays     = events.filter(e => e.type === 'play');
    const views     = events.filter(e => e.type === 'view');
    const clicks    = events.filter(e => e.type === 'click');
    const downloads = events.filter(e => e.type === 'download');
    const shares    = events.filter(e => e.type === 'share');
    const favorites = events.filter(e => e.type === 'favorite');

    // Identify demo/beat tracks so they're listed separately from released music.
    const DEMO_TYPES = ['Beat', 'Demo', 'Beat Pack', 'Instrumental'];
    // Artists whose catalogue is demos/beats — name fallback in case a work's
    // type tag is missing/wrong (e.g. NUFF BEATS).
    const DEMO_ARTIST_NAMES = new Set(['nuff beats']);
    const demoIds = new Set<string>();
    try {
      const demoSnap = await getDocs(query(collection(db, 'works'), where('type', 'in', DEMO_TYPES)));
      demoSnap.docs.forEach(d => demoIds.add(d.id));
    } catch { /* no index / offline — treat everything as released */ }
    const isDemo = (id: string, artist?: string) =>
      demoIds.has(id) || DEMO_ARTIST_NAMES.has((artist || '').toLowerCase().trim());

    // Top tracks by play count — released vs demos
    const trackPlayMap = new Map<string, { entityId: string; entityTitle: string; entityArtist: string; plays: number }>();
    const demoPlayMap  = new Map<string, { entityId: string; entityTitle: string; entityArtist: string; plays: number }>();
    plays.filter(e => e.entityType === 'track').forEach(e => {
      const target = isDemo(e.entityId, e.entityArtist) ? demoPlayMap : trackPlayMap;
      const existing = target.get(e.entityId) || { entityId: e.entityId, entityTitle: e.entityTitle || 'Unknown', entityArtist: e.entityArtist || 'Unknown', plays: 0 };
      existing.plays++;
      target.set(e.entityId, existing);
    });
    const topTracks = Array.from(trackPlayMap.values()).sort((a, b) => b.plays - a.plays).slice(0, 10);
    const topDemos  = Array.from(demoPlayMap.values()).sort((a, b) => b.plays - a.plays).slice(0, 10);

    // Top artists — released vs demo artists (split by whether the played track is a demo)
    const artistPlayMap     = new Map<string, { entityId: string; entityTitle: string; plays: number }>();
    const demoArtistPlayMap = new Map<string, { entityId: string; entityTitle: string; plays: number }>();
    plays.filter(e => e.entityType === 'track' && e.entityArtist).forEach(e => {
      const key = e.entityArtist!;
      const target = isDemo(e.entityId, e.entityArtist) ? demoArtistPlayMap : artistPlayMap;
      const existing = target.get(key) || { entityId: key, entityTitle: key, plays: 0 };
      existing.plays++;
      target.set(key, existing);
    });
    const topArtists     = Array.from(artistPlayMap.values()).sort((a, b) => b.plays - a.plays).slice(0, 10);
    const topDemoArtists = Array.from(demoArtistPlayMap.values()).sort((a, b) => b.plays - a.plays).slice(0, 10);

    // Top genres
    const genrePlayMap = new Map<string, number>();
    plays.filter(e => e.entityGenre).forEach(e => {
      genrePlayMap.set(e.entityGenre!, (genrePlayMap.get(e.entityGenre!) || 0) + 1);
    });
    const topGenres = Array.from(genrePlayMap.entries())
      .map(([genre, plays]) => ({ genre, plays }))
      .sort((a, b) => b.plays - a.plays)
      .slice(0, 10);

    // The trend covers six calendar months, while engagement totals use limitDays.
    const monthPlayMap: Record<string, { plays: number; downloads: number }> = Object.fromEntries(months.map(m => [m.month, { plays: 0, downloads: 0 }]));
    validEvents.forEach(e => {
      const date = catalogueDate(e.timestamp);
      if (!date) return;
      const key = date.toLocaleString('en', { month: 'short', year: '2-digit' });
      if (!(key in monthPlayMap)) return;
      if (e.type === 'play')     monthPlayMap[key].plays++;
      if (e.type === 'download') monthPlayMap[key].downloads++;
    });
    const playsOverTime = Object.entries(monthPlayMap).map(([month, v]) => ({ month, ...v }));

    return {
      totalPlays: plays.length,
      totalViews: views.length,
      totalClicks: clicks.length,
      totalDownloads: downloads.length,
      totalShares: shares.length,
      totalFavorites: favorites.length,
      topTracks,
      topArtists,
      topDemos,
      topDemoArtists,
      topGenres,
      recentEvents: events.slice(0, 20),
      playsLast7Days:  plays.filter(e => now - (catalogueDate(e.timestamp)?.getTime() ?? 0) <= ms7).length,
      playsLast30Days: plays.filter(e => now - (catalogueDate(e.timestamp)?.getTime() ?? 0) <= ms30).length,
      playsOverTime,
    };
  } catch (error) {
    if (throwOnError) throw error;
    return {
      totalPlays: 0, totalViews: 0, totalClicks: 0,
      totalDownloads: 0, totalShares: 0, totalFavorites: 0,
      topTracks: [], topArtists: [], topDemos: [], topDemoArtists: [], topGenres: [],
      recentEvents: [], playsLast7Days: 0, playsLast30Days: 0,
      playsOverTime: [],
    };
  }
}
